import { IonIcon } from "@ionic/react";
import { arrowUndoOutline, returnDownBackOutline } from "ionicons/icons";
import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { usePersistedSliceSelector } from "../../store/persistence";
import { displayCurrencyToIsEuro, pricingPrefsSlice } from "../../store/persistence/slices/pricingPrefs.slice";
import PriceConversion from "../ItinerariesPlanner/pricing/PriceConversion";
import { FOOTER_FLEX_GROW, notchMask, STUB_FLEX_GROW } from "./ticketCardSkin";
import { resolveTicketProviderBadge } from "./ticketProvider.tools";
import { TicketProviderBadgeMark } from "./TicketProviderBadgeMark";
import { formatPaymentMethod, formatTicketDescription, formatTicketSalesDate, formatTicketVat, type TicketViewerItem } from "./ticketViewer.tools";

/**
 * Props of {@link TicketCardBack}.
 */
export interface TicketCardBackProps {
    "item": TicketViewerItem;

    /** Backend provider id — shown as the `Fournisseur` meta line. */
    "provider": string;

    /** Whether the back face is currently shown — drives `aria-hidden` + tab order. */
    "active":           boolean;
    "onFlipBack"?:      () => void;
    "onRequestRefund"?: () => void;
}

/** One label → value row of the meta block (`value` may be a price component). */
interface MetaLine {
    "label": string;
    "value": React.ReactNode;
}

/**
 * `mask-image` value fading the scrollable description edge(s) that still hide
 * content — the visual cue that the legal text continues, without a scrollbar.
 */
const descriptionMask = {
    "none":   undefined,
    "bottom": "linear-gradient(to bottom, black calc(100% - 2.5rem), transparent)",
    "top":    "linear-gradient(to bottom, transparent, black 2.5rem)",
    "both":   "linear-gradient(to bottom, transparent, black 2.5rem, black calc(100% - 2.5rem), transparent)",
} as const;

/**
 * Verso of the ticket card — revealed by the 3D flip: product title, the meta
 * block (price incl. VAT, sale date, zones, ticket identifiers) and the legal
 * product description in a scrollable area with a fade affordance.
 *
 * @remarks The paper palette is fixed (slate on `#fdfdfb`) — like the front,
 * the card stays light in both color schemes. The silhouette mirrors the front
 * exactly: same section split and punched notches so the card contour does not
 * change while turning. Clicks anywhere outside the scrollable text flip back.
 * @param props - Component props.
 * @returns The back face.
 */
export const TicketCardBack: React.FC<TicketCardBackProps> = ( {
    item,
    provider,
    active,
    onFlipBack,
    onRequestRefund,
} ) => {
    const isEuro = usePersistedSliceSelector( pricingPrefsSlice, ( prefs ) => displayCurrencyToIsEuro( prefs.displayCurrency ) );
    const details = item.details ?? null;
    const description = formatTicketDescription( details?.description ?? "" );
    const refundStatus = item.ticket.refundStatus ?? "none";
    const isRefunded = refundStatus === "refunded" || refundStatus === "refund_pending";
    const refundedAmountCents = item.ticket.refundedAmountCents ?? null;

    // Fade the description edge that still hides content — tracks scroll + size.
    const descriptionRef = useRef<HTMLParagraphElement>( null );
    const [ scrollFade, setScrollFade ] = useState<keyof typeof descriptionMask>( "none" );
    const updateScrollFade = useCallback( () => {
        const element = descriptionRef.current;

        if ( element === null || element.scrollHeight - element.clientHeight <= 2 ) {
            setScrollFade( "none" );

            return;
        }

        const up = element.scrollTop > 2;
        const down = element.scrollTop + element.clientHeight < element.scrollHeight - 2;

        setScrollFade( up && down ? "both" : up ? "top" : down ? "bottom" : "none" );
    }, [] );

    useLayoutEffect( () => {
        const element = descriptionRef.current;

        if ( element === null ) {
            return;
        }

        // `observe` fires an initial async callback — no sync setState needed.
        if ( typeof ResizeObserver === "undefined" ) {
            const frame = requestAnimationFrame( updateScrollFade );

            return () => cancelAnimationFrame( frame );
        }

        const observer = new ResizeObserver( updateScrollFade );

        observer.observe( element );

        return () => observer.disconnect();
    }, [ description, updateScrollFade ] );

    const salesDate = details?.salesDate === undefined ? "" : formatTicketSalesDate( details.salesDate );
    const purchaseLines = ( [
        {
            "label": "Fournisseur",
            "value": resolveTicketProviderBadge( provider ).label,
        },
        details?.priceCents !== undefined
            ? {
                "label": "Prix",
                "value": (
                    <>
                        {details.vatPercent === undefined
                            ? null
                            : (
                                <span className="mr-0.5 text-[0.6rem] font-normal text-slate-400">
                                    {`(incl. TVA ${ formatTicketVat( details.vatPercent ) }) `}
                                </span>
                            )}
                        <PriceConversion
                            chooseEuro={isEuro}
                            price={details.priceCents / 100}
                        />
                    </>
                ),
            }
            : null,
        salesDate !== ""
            ? {
                "label": "Vendu",
                "value": salesDate,
            }
            : null,
        formatPaymentMethod( item.paymentMethod ) !== ""
            ? {
                "label": "Paiement",
                "value": formatPaymentMethod( item.paymentMethod ),
            }
            : null,
        isRefunded
            ? {
                "label": "Statut",
                "value": (
                    <span
                        className="font-bold text-amber-700"
                        data-testid="ticket-meta-refund-status"
                    >
                        {refundStatus === "refund_pending" ? "Remboursement en cours" : "Remboursé"}
                        {refundedAmountCents !== null && refundedAmountCents > 0
                            ? (
                                <>
                                    {" ("}
                                    <PriceConversion
                                        chooseEuro={isEuro}
                                        price={refundedAmountCents / 100}
                                    />
                                    )
                                </>
                            )
                            : null}
                    </span>
                ),
            }
            : null,
    ] as ( MetaLine | null )[] ).filter( ( line ): line is MetaLine => line !== null );

    const ticketLines = ( [
        details?.validityAreaSummary !== undefined
            ? {
                "label": "Zone(s)",
                "value": details.validityAreaSummary,
            }
            : null,
        {
            "label": "Ticket-ID",
            "value": item.ticket.externalTicketId,
        },
        {
            "label": "No référence",
            "value": item.ticket.externalTicketReference,
        },
        item.productId != null
            ? {
                "label": "No d'article",
                "value": item.productId,
            }
            : null,
    ] as ( MetaLine | null )[] ).filter( ( line ): line is MetaLine => line !== null );

    /*
     * Purchase info and ticket identity live in two bubbles — the split mirrors
     * the commercial-vs-control blocks of a real ticket. 
     */
    const metaGroups: ReadonlyArray<readonly [ string, MetaLine[] ]> = [
        [ "ticket-info-meta", purchaseLines ],
        [ "ticket-info-meta-ids", ticketLines ],
    ];

    return (
        <div
            aria-hidden={!active}
            aria-label="Verso du billet"
            className="flex h-full w-full cursor-pointer flex-col"
            data-testid="ticket-card-back"
            onClick={onFlipBack}
            role="region"
        >
            {/*
             * Stub section — same silhouette as the front: the masks punch the
             * bottom corners so the flip never changes the card outline.
             */}
            <div
                className="flex min-h-0 flex-col"
                style={{
                    "flex": STUB_FLEX_GROW,
                    ...notchMask( "0%", "100%" ),
                }}
            >
                <div
                    className="flex min-h-0 flex-1 flex-col bg-[#fdfdfb]"
                    style={notchMask( "100%", "100%" )}
                >
                    {/* Header: same provider mark as the front — the ticket reference stays recto-only. */}
                    <div className="flex items-center justify-between gap-2 px-4 pt-4">
                        <TicketProviderBadgeMark provider={provider} />
                    </div>
                    <p className="mx-4 mt-3 min-w-0 truncate text-sm font-bold text-slate-900">
                        {details?.productDescription ?? "Détails du billet"}
                    </p>
                    {metaGroups.map( ( [ testId, lines ] ) => (
                        lines.length > 0
                            ? (
                                <div
                                    className="mx-4 mt-2 flex flex-col gap-1 rounded-xl bg-black/[0.04] p-3 text-xs"
                                    data-testid={testId}
                                    key={testId}
                                >
                                    {lines.map( ( line ) => (
                                        <p
                                            className="flex items-baseline justify-between gap-3"
                                            key={line.label}
                                        >
                                            <span className="shrink-0 font-medium text-slate-500">
                                                {line.label}
                                            </span>
                                            <span
                                                className="min-w-0 truncate text-right font-semibold text-slate-800"
                                                data-testid={`ticket-meta-${ line.label }`}
                                                title={typeof line.value === "string" ? line.value : undefined}
                                            >
                                                {line.value}
                                            </span>
                                        </p>
                                    ) )}
                                </div>
                            )
                            : null
                    ) )}
                </div>
            </div>
            {/*
             * Footer section — perforation kept for silhouette continuity, then
             * the legal description (scrollable, wheel-guarded) and the return
             * button at the info button's mirror position.
             */}
            <div
                className="flex min-h-0 flex-col"
                style={{
                    "flex": FOOTER_FLEX_GROW,
                    ...notchMask( "0%", "0%" ),
                }}
            >
                <div
                    className="flex min-h-0 flex-1 flex-col bg-[#fdfdfb]"
                    style={notchMask( "100%", "0%" )}
                >
                    <div className="shrink-0 border-t-2 border-dashed border-slate-300" />
                    <p
                        className="min-h-0 flex-1 cursor-text overflow-y-auto whitespace-pre-line px-4 pt-3 text-xs leading-relaxed text-slate-500 [&::-webkit-scrollbar]:hidden"
                        data-card-scroll=""
                        data-testid="ticket-info-description"
                        onClick={( event ) => event.stopPropagation()}
                        onScroll={updateScrollFade}
                        ref={descriptionRef}
                        style={{
                            "maskImage":       descriptionMask[scrollFade],
                            "scrollbarWidth":  "none",
                            "WebkitMaskImage": descriptionMask[scrollFade],
                        }}
                    >
                        {description}
                    </p>
                    <div className="flex shrink-0 items-center justify-between gap-2 px-4 pb-3 pt-1">
                        {!isRefunded && onRequestRefund !== undefined
                            ? (
                                <button
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50/80 px-2.5 py-1 text-[11px] font-bold text-red-600 transition-colors duration-150 hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2"
                                    data-testid="ticket-refund-button"
                                    onClick={( event ) => {
                                        event.stopPropagation();
                                        onRequestRefund();
                                    }}
                                    tabIndex={active ? 0 : -1}
                                    type="button"
                                >
                                    <IonIcon
                                        aria-hidden={true}
                                        className="h-3.5 w-3.5 shrink-0"
                                        icon={returnDownBackOutline}
                                    />
                                    Rembourser ce billet
                                </button>
                            )
                            : <span />}
                        <button
                            aria-label="Retour au recto"
                            className="-mb-1 -mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors duration-150 hover:bg-slate-100 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2"
                            data-testid="ticket-back-button"
                            onClick={( event ) => {
                                event.stopPropagation();
                                onFlipBack?.();
                            }}
                            tabIndex={active ? 0 : -1}
                            type="button"
                        >
                            <IonIcon
                                aria-hidden={true}
                                className="h-[18px] w-[18px]"
                                icon={arrowUndoOutline}
                            />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
